import { tryUntilSuccess } from 'app/lib/utils/RequestUtils';
import jwt_decode from 'jwt-decode';
import AppEvents from 'lib/AppEvents';
import Constants from 'lib/Constants';
import { recalculate_cache_key_dates } from 'lib/data/CacheKeys';
import { Account } from 'lib/data/models/Account';
import AuthService from 'lib/services/AuthService';
import NotificationService from 'lib/services/NotificationService';
import GoogleService from 'lib/services/providers/GoogleService';
import { getAccountProviders, handleResponseErrorNotifications } from 'lib/utils/adaya/AdayaUtils';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import AccountService from 'services/AccountService';
import AppService from 'services/AppService';
import MetricService from 'services/data/MetricService';
import EventService from 'services/EventService';
import FacebookService from 'services/providers/FacebookService';
import UserService from 'services/UserService';
import Cookies from 'utils/Cookies';
import { setGlobalCurrency } from 'utils/currency';
import { recalculate_core_dates, setGlobalTimezone } from 'utils/DateUtils';
import { debug } from 'utils/debug';
import { getLogger } from 'utils/logging';
import { AuthContext } from './contexts/AuthContext';
import { DEMO_MODE, demoUser } from 'app/demo/demoConfig';

const { log, warn } = getLogger('AuthProvider', { color: 'cyan', enabled: false });

// fu react
let userInitializedCheck = false;
let appDataInitializedCheck = false;

// These get loaded at app init, calls 'updateData' on each.
// const DEFAULT_PROVIDERS = [
//     { name: 'Facebook', provider: FacebookDataProvider },
//     { name: 'Account', provider: AccountDataProvider },
//     { name: 'Shopify', provider: ShopifyDataProvider },
//     { name: 'Adspend', provider: AdspendDataProvider },
//     { name: 'GoogleAds', provider: GoogleDataProvider }
// ];

// todo: move these to individual provider config

const checkLoginProviders = {
    'Facebook': async (user) => {
        return await FacebookService.checkLogin(user);
    },

    'Google': async (user) => {
        return await GoogleService.checkLogin(user);
    }
}

const loginProviders = {
    'Facebook': async (user, forcePrompt = false) => {
        let r = forcePrompt ?
            await FacebookService.login(user) :
            await FacebookService.assertLogin(user);
        if (!r) {
            return false;
        }
        return true;
    },

    'Google': async (user, forcePrompt = false) => {
        let r = forcePrompt ?
            await GoogleService.login(user) :
            await GoogleService.assertLogin(user);
        if (!r) {
            return false;
        }
        return true;
    }
}

const logoutProviders = {
    'Facebook': async (user) => {
        let r = await FacebookService.logout(false); // don't actually logout of FB (this is through automated means)
        return true;
    },

    'Google': async (user) => {
        let r = await GoogleService.logout(user);
        return true;
    }
}

const AuthProvider = (props) => {

    const [authState, setAuthState] = useState({
        // PROPS ---------------
        authLoaded: false,      // when the auth libraries are loaded for use anywhere.
        authFinished: false,    // when the silent auth has been completed and determined.
        providerAuthFinished: false,
        loginRequired: false,

        loggedIn: false,
        loggedInProviders: false,
        appDataInitialized: false, // allows us to protect against multiple calls to updateAppData when state changes,
        pendoInitialized: false,
        userInitialized: false,

        user: null,             // reference to the basic user
        accessToken: null,      // basic account access token
        accountId: null,        // todo: change to Account object

        // METHODS -------------
        login: null,
        logout: null,
        signup: null,
        updateUser: null,
        updatePaymentMethod: null,
        //updateAppData: null,
        updateUserLocal: null,
        updateAccount: null,
        silentAuth: null,

        getAccountAge: null,
        updateAccountAge: null
    })

    // Initialize the auth system upon load, and do a silent authentication.
    useEffect(async () => {
        // load auth libraries
        await AuthService.init();

        bindEvents();

        updateAuthState({ authLoaded: true });
    }, []);

    // have to separate here so child components get updated state properly.
    useEffect(async () => {
        if (authState.authLoaded) {

            // ---- PORTFOLIO DEMO SHORT-CIRCUIT ----
            // Skip real soft/silent/provider auth. Inject a fake logged-in user
            // so the whole product is clickable against dummy data.
            if (DEMO_MODE) {
                console.log('🎭 DEMO_MODE: injecting mock logged-in user', demoUser);
                await AuthService.updateCurrentUser(demoUser);
                return updateAuthState({
                    loggedIn: true,
                    loggedInProviders: true,
                    providerAuthFinished: true,
                    authFinished: true,
                    user: demoUser,
                });
            }
            // --------------------------------------

            let user = AuthService.getCurrentUser();

            // check local token first
            if (!softAuth() || !user) {
                log('Soft auth failed.');
                if (user) debug(user, 'Soft auth failed');
                return updateAuthState({
                    loggedIn: false,
                    authFinished: true
                });
            } else {
                // update but let it continue
                updateAuthState({
                    loggedIn: true,
                    user
                });
            }

            log('Soft auth passed.', user);
            debug(user, 'Soft auth passed.');

            // do silent adaya and silent provider auth in parallel:
            const [silentAuthed, silentAuthedProviders] = await Promise.all([
                silentAuth(),
                silentAuthProviders(user)
            ]);

            // silent auth finished
            if (silentAuthed) {
                log('Silent auth passed.');
                debug(user, 'Silent auth passed.');
            } else {
                log('Silent auth failed, logging out.');
                debug(user, 'Silent auth failed.');
                // should logout the user?
                return updateAuthState({
                    loggedIn: false,
                    authFinished: true
                });
                await this.logout();
            }

            // provider auth finished
            if (silentAuthedProviders) {
                log('Provider auth passed.');
                debug(user, 'Provider auth passed.');
                updateAuthState({
                    providerAuthFinished: true,
                    loggedInProviders: true,
                    authFinished: true
                });
            } else {
                log('Provider auth failed.');
                debug(user, 'Provider auth failed.');
                return updateAuthState({
                    providerAuthFinished: true,
                    loggedInProviders: false,
                    authFinished: true
                });
            }
        }
    }, [authState.authLoaded]);

    useEffect(async () => {
        // ensure user is initialized on auth state changes:
        if (authState.user && authState.loggedIn && authState.loggedInProviders && !authState.userInitialized && !userInitializedCheck) {
            userInitializedCheck = true;
            await initializeUser(authState.user);
            updateAuthState({ userInitialized: true });
        }

        // then update app data once when all is good
        if (authState.user && authState.loggedIn && authState.loggedInProviders && authState.userInitialized && !authState.appDataInitialized && !appDataInitializedCheck) {
            appDataInitializedCheck = true;
            //await updateAppData();
            updateAuthState({ appDataInitialized: true });
        }
    }, [authState.user, authState.loggedIn, authState.loggedInProviders, authState.userInitialized]);

    const bindEvents = () => {
        // subscribe to logout events (children may throw), and force logout:
        EventService.subscribe(AppEvents.NEEDS_LOGOUT, logout);
        EventService.subscribe(AppEvents.CHANGE_ACCOUNT, changeActiveAccount);
        return () => {
            EventService.unsubscribe(AppEvents.NEEDS_LOGOUT, logout);
            EventService.unsubscribe(AppEvents.CHANGE_ACCOUNT, changeActiveAccount);
        }
    }

    // helper method to combine new state with previous
    const updateAuthState = (state) => {
        setAuthState(prevState => ({
            ...prevState,
            ...state,
        }))
    }

    const initializeUser = async (user, forceInit = false) => {
        log('initializeUser', user);
        if (!authState.pendoInitialized || forceInit) {
            initializePendo(user);
        }

        setGlobals(user);
        updateAccountAge(user);

        await AuthService.updateCurrentUser(user);

        updateAuthState({
            loggedIn: true,
            authFinished: true,
            appDataInitialized: false,
            user
        });

        console.log(`🧑 USER loaded:`, user);
    }

    const initializePendo = async (user) => {
        try {
            const _init = () => {
                log(`init pendo.`)
                if (pendo) {
                    pendo.initialize({
                        visitor: {
                            id: user._id,
                            email: user.email,
                            full_name: user.name,
                            role: user.roles ? user.roles.join(',') : ['user'],
                            creation_date: user.dateJoined,
                            last_login: user.lastLogin
                            // You can add any additional visitor level key-values here,
                            // as long as it's not one of the above reserved names.
                        },

                        account: {
                            id: user.activeAccount._id,
                            name: user.activeAccount.name,
                            // is_paying:    // Recommended if using Pendo Feedback
                            // monthly_value:// Recommended if using Pendo Feedback
                            // planLevel:    // Optional
                            // planPrice:    // Optional
                            // creationDate: // Optional
                            creationDate: user.activeAccount.tracking?.dateCreated || user.dateJoined,

                            // You can add any additional account level key-values here,
                            // as long as it's not one of the above reserved names.
                        }
                    });

                    updateAuthState({ pendoInitialized: true });
                }
            }

            const checkPendo = async () => {
                return typeof pendo != 'undefined';
            }

            let hasPendo = await tryUntilSuccess(checkPendo, { retries: 10 });
            if (hasPendo) {
                _init();
            }
        } catch (e) {
            console.error("Error initializing Pendo:", e);
        }
    }

    const completeSuccessfulLogin = async (user) => {
        //await initializeUser(user);
        log(`completeSuccessfulLogin`, user);
        if (user) {
            updateAuthState({ loggedIn: true, user });
            await AppService.verifyAppVersion();
            debug(user, 'User logged in.', { user, appVersion: window.adayaAppVersion });
        } else {
            throw "No user given to completeSuccessfulLogin"
        }
    }

    const setGlobals = async (user) => {
        try {
            if (user.activeAccount && user.activeAccount.storeInfo) {
                let { currency, timezone } = user.activeAccount.storeInfo;

                if (!currency) {
                    currency = await UserService.updateAccountCurrency(user);
                }

                // set global currency code
                setGlobalCurrency(currency);

                if (timezone) {
                    setGlobalTimezone(timezone);
                }

                // reconfigure app dates and cache keys
                recalculate_core_dates(timezone);
                recalculate_cache_key_dates();
            } else {
                // setup the dates/keys anyway
                recalculate_core_dates();
                recalculate_cache_key_dates();
            }
        } catch (error) {
            console.log(error);
        }
    }

    // Updates all provider data for the app/account.
    // async function updateAppData(providerNames) {
    //     try {
    //         if (authState.loggedInProviders || providerNames) {
    //             // use user's providers, pulled from list of defaults.
    //             let providers = DEFAULT_PROVIDERS;
    //             if (providerNames) {
    //                 providers = providers.filter(p => providerNames.includes(p.name));
    //             }

    //             // update all providers
    //             let result = await Promise.allSettled(providers.map(p =>
    //                 new Promise(async (resolve, reject) => {
    //                     try {
    //                         log('Updating provider data:', p.name)
    //                         let r = await p.provider.updateData(authState.user,
    //                             (update) => {
    //                                 switch (update.status) {
    //                                     case DataStatus.PROGRESS:
    //                                         EventService.dispatch(AppEvents.PROVIDER_PROGRESS_UPDATE, {
    //                                             status: DataStatus.PROGRESS,
    //                                             provider: p.name,
    //                                             id: update.id,
    //                                             progress: update.progress,
    //                                             message: update.message,
    //                                         });
    //                                         break;

    //                                     case DataStatus.READY:
    //                                         break;

    //                                     case DataStatus.ALL_COMPLETE:
    //                                         break;
    //                                 }
    //                             },
    //                             (complete) => { },
    //                             updateIfCached = false
    //                         );
    //                         log('Provider update complete:', p.name, r);
    //                         return resolve(r);
    //                     } catch (e) {
    //                         console.error('! Provider update exception: ', p.name, e)
    //                         return reject(e);
    //                     }
    //                 })
    //             ));
    //         }
    //     } catch (e) {
    //         console.log('Exception loading app data:', e);
    //         debug(authState.user, 'All provider update exception.', providers.map(p => p.name));
    //         throw e;
    //     }
    // }

    // Validates the existence of the local access token "immediately"
    const softAuth = async () => {

        let token = Cookies.get(Constants.ACCESS_TOKEN);
        if (token) {
            try {
                let decoded = jwt_decode(token);
                let expDate = new Date(decoded.exp * 1000);
                if (expDate < new Date()) {
                    return false; // must login
                }

                // we also do an app version check:
                await AppService.verifyAppVersion();

                return true;
            } catch (e) {
                console.log(`Exception decoding access token:`, e);
                return false;
            }
        }
        return false;
    }

    // Validate the token with the backend server, legitimately.
    const silentAuth = async () => {
        try {
            let user = await AuthService.checkSession();
            if (user) {
                updateUserLocal(user);
                return user;
            }
            return null;
        } catch (e) {
            console.log(`Exception in AuthProvider`, e)
            handleResponseErrorNotifications(e);
        }
    }

    // We pass user because the event system mixed with props sometimes doesn't have correct state when using these methods.
    const silentAuthProviders = async (user?) => {
        // todo: get providers from user account.
        let providers = getAccountProviders(user.activeAccount);
        if (!user) user = AuthService.getCurrentUser();

        for (const p of providers) {
            log("check provider", p);
            if (!await checkLoginProvider(p.name, user)) {
                return false;
            }
        }

        return true;
    }

    const login = async (email, password, redirect, isMockAccount) => {
        return new Promise(async (resolve, reject) => {
            try {
                let r = await AuthService.login(email, password, redirect, isMockAccount)
                if (!r.success) {
                    return reject(r);
                } else {
                    AppService.setAppVersion();

                    // for now we put the store info (for active account) on the user object to store it:
                    await completeSuccessfulLogin(r.user);

                    return resolve(r);
                }
            } catch (e) {
                return reject(e);
            }
        });
    }

    // checks if the given provider is logged in
    const checkLoginProvider = async (provider: string, user?) => {
        try {
            if (checkLoginProviders[provider] && checkLoginProviders[provider] !== 'Google') {
                return await checkLoginProviders[provider](user);
            }
            // consider providers that don't implement the interface ok
            return true;
        } catch (e) {
            throw e;
        }
    }

    // initiates login for the given provider
    const loginProvider = async (provider, user?, forcePrompt = false) => {
        try {
            if (loginProviders[provider]) {
                return await loginProviders[provider](user, forcePrompt);
            }
        } catch (e) {
            throw e;
        }
    }

    // tries to disconnect a provider
    const disconnectProvider = async (account, p) => {
        try {
            if (!account) return console.log(`No account given to disconnect.`);
            if (!p || !p?.name) return console.log(`No provider given to disconnect.`)

            console.log(`Disconnecting provider...`, account, p)

            // update the account's provider
            let newUser = await AccountService.disconnectAccountProvider(account._id, p);

            if (newUser) {
                await updateUserLocal(newUser);
                NotificationService.notifySuccess('Success', `The ${p.name} account has been disconnected.`);
            } else {
                console.warn("Failed updating user from provider disconnect?", account, p, deleteResult);
                NotificationService.notifyError('Error', 'Unexpected error disconnecting account.');
            }

            return newUser;
        } catch (e) {
            throw e;
        }
    }

    const changeActiveAccount = async (event) => {
        const toAccount = event.target;
        if (toAccount && toAccount._id) {
            try {
                const updated = await AuthService.changeActiveAccount(toAccount._id);
                if (updated) {
                    await AuthService.clearData();
                    await initializeUser(updated, true);
                } else {
                    NotificationService.notifyError('Error', 'Unexpected error changing the account');    
                }
            } catch (error) {
                console.log('Changing active account error', error);
                NotificationService.notifyError('Error', 'Unexpected error changing the account');
            }
        } else {
            NotificationService.notifyError('Error', 'Please choose an account to change.');
        }
    }

    // initiates login for the given provider
    const setProvidersLoggedIn = async (loggedIn) => {
        updateAuthState({ loggedInProviders: loggedIn });
    }

    const signup = (profile, goals, paymentMethod, fbAccount, fbToken, fbPixel, fbUserId?) => {
        return new Promise((resolve, reject) => {
            AuthService.signup(profile, goals, paymentMethod, fbAccount, fbToken, fbPixel, fbUserId)
                .then(r => {
                    if (!r.success) {
                        return reject(r);
                    } else {
                        return resolve(r);
                    }
                })
                .catch(e => {
                    log('signup error', e);
                    return reject(e);
                });
        });
    }

    const acceptInvite = (token, name, password) => {
        return new Promise((resolve, reject) => {
            AuthService.acceptInvite(token, name, password)
                .then((r) => {
                    if (!r.success) {
                        return reject(r);
                    } else {
                        return resolve(r);
                    }
                })
                .catch((e) => {
                    return reject(e);
                });
        });
    };

    const forgotPassword = email => {
        return new Promise((resolve, reject) => {
            AuthService.forgotPassword(email)
                .then((r) => {
                    if (!r.success) {
                        return reject(r);
                    } else {
                        return resolve(r);
                    }
                })
                .catch((e) => {
                    return reject(e);
                });
        });
    };

    const resetPassword = (token, password) => {
        return new Promise((resolve, reject) => {
            AuthService.resetPassword(token, password)
                .then((r) => {
                    if (!r.success) {
                        return reject(r);
                    } else {
                        return resolve(r);
                    }
                })
                .catch((e) => {
                    return reject(e);
                });
        });
    };

    const logout = async (redirect?) => {
        debug(authState.user, 'Logging out.');

        // logout user's providers:
        // todo: get providers and call logout methods...
        if (authState.user && authState.user.activeAccount) {
            await Promise.all(
                authState.user.activeAccounts.providers.map(async p => {
                    let method = logoutProviders[p.name];
                    if (method) {
                        await method();
                    }
                })
            );
        }

        AuthService.logout(redirect);

        updateAuthState({
            user: undefined,
            loggedIn: false,
            loggedInProviders: false,
            appDataInitialized: false
        });
    }

    const updateAccount = async (account): Account => {
        return new Promise(async (resolve, reject) => {
            try {
                log(`updateAccount`, account)
                account = await AccountService.updateAccount(account._id, account);
                if (account) {
                    await silentAuth(); // reload user/account
                    EventService.dispatch(AppEvents.ACCOUNT_UPDATED, { account });
                    return resolve(account);
                } else {
                    return reject('Account not saved.');
                }
            } catch (error) {
                return reject(error);
            }
        });
    }

    // TODO: temp solution before storing data in backend users
    const updateUser = (u) => {
        return new Promise(async (resolve, reject) => {
            let user = await UserService.updateUser(u);
            if (user) {
                await updateUserLocal(user);
                return resolve(user);
            } else {
                return reject();
            }
        });
    }

    const updateUserLocal = async (user) => {
        await AuthService.updateCurrentUser(user);
        await updateAuthState({ user });
        setGlobals(user); // we reset the global in case the provider info changed, ie. timezone or currency, but it could be done better.
    }

    const updatePaymentMethod = (pm) => {
        return new Promise(async (resolve, reject) => {
            let user = await UserService.updatePaymentMethod(pm);
            if (user) {
                updateAuthState({ user });
                return resolve(user);
            } else {
                return reject();
            }
        });
    }

    const getAccountAge = (user?) => {
        let age = MetricService.get('accountAgeDays', 1);
        user = user || authState.user;

        if (user) {
            if (user.dateJoined) {
                _age = moment().diff(moment(user.dateJoined), 'days') + 1;
                if (age != _age) {
                    updateAccountAge(user);
                    age = _age;
                }
            } else {
                // assume max if an old user/no date joined property...
                if (age < 8) {
                    updateAccountAge(user);
                    age = 8;
                }
            }
        }

        return age;
    }

    const updateAccountAge = (user) => {
        let age = 8; // assume max

        if (user.dateJoined) {
            age = moment().diff(moment(user.dateJoined), 'days') + 1;
        }

        // keep their account age updated (every page refresh)
        MetricService.set('accountAgeDays', age);

        return age;
    }

    // todo? move specific implementation to sub-objects, ie. auth.login, auth.logout, user.updateProfile, user.updatePaymentMethod, etc...

    // assign the definitions to the global object for other components to use:
    if (!authState.login &&
        !authState.logout &&
        !authState.signup &&
        !authState.updateUser &&
        !authState.updatePaymentMethod &&
        !authState.checkLoginProvider &&
        !authState.loginProvider &&
        !authState.setProvidersLoggedIn &&
        !authState.disconnectProvider &&
        //!authState.updateAppData &&
        !authState.updateUserLocal &&
        !authState.updateAccount &&
        !authState.getAccountAge &&
        !authState.updateAccountAge &&
        !authState.acceptInvite &&
        !authState.forgotPassword &&
        !authState.resetPassword &&
        !authState.silentAuth &&
        !authState.changeActiveAccount
    ) {
        updateAuthState({
            login,
            logout,
            signup,
            updateUser,
            updatePaymentMethod,
            checkLoginProvider,
            loginProvider,
            setProvidersLoggedIn,
            disconnectProvider,
            //updateAppData,
            updateUserLocal,
            updateAccount,
            getAccountAge,
            updateAccountAge,
            acceptInvite,
            forgotPassword,
            resetPassword,
            silentAuth,
            changeActiveAccount
        });
    }

    //let fbAppId = IS_DEV() ? process.env.FB_TEST_APP_ID : process.env.FB_APP_ID;
    let fbAppId = process.env.FB_APP_ID;

    return (
        <AuthContext.Provider value={authState}>
            <Helmet>
                <script crossOrigin="anonymous" src={`https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v10.0&appId=${fbAppId}&autoLogAppEvents=1`} />
            </Helmet>
            {props.children}
        </AuthContext.Provider>
    )
}

export default AuthProvider;